import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";

/**
 * Lightweight contenteditable rich text editor.
 * Stores HTML in `value` and triggers `onChange` on input.
 * Supports image paste/upload as inline base64 <img> tags.
 */
export default function RichEditor({ value, onChange, placeholder = "Write here...", testId }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = DOMPurify.sanitize(value || "");
      updateEmpty(ref.current);
    }
    // Initial-mount sync only; subsequent input handled by onInput.
  }, []);

  const updateEmpty = (el) => {
    el.dataset.empty = (el.innerText.trim() === "" && !el.querySelector("img")) ? "true" : "false";
  };

  const handleInput = () => {
    const el = ref.current;
    if (!el) return;
    updateEmpty(el);
    onChange(el.innerHTML);
  };

  const insertImage = (dataUrl) => {
    const editor = ref.current;
    if (!editor) return;
    editor.focus();
    // Restore selection to end of editor if focus didn't carry a range (file picker steals focus)
    const sel = window.getSelection();
    if (!sel.rangeCount || !editor.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    const img = `<img src="${dataUrl}" alt="screenshot" />`;
    const safeImg = DOMPurify.sanitize(img, { ADD_TAGS: ["img"], ADD_ATTR: ["src", "alt"] });
    const inserted = document.execCommand("insertHTML", false, safeImg);
    if (!inserted) {
      // Fallback for browsers/contexts where execCommand fails
      editor.insertAdjacentHTML("beforeend", safeImg);
    }
    handleInput();
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => insertImage(ev.target.result);
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => insertImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleEditorClick = (e) => {
    const editor = ref.current;
    if (!editor) return;
    // Clear previous image selection
    editor.querySelectorAll("img.is-selected").forEach((n) => n.classList.remove("is-selected"));
    if (e.target?.tagName === "IMG") {
      e.target.classList.add("is-selected");
      // Programmatically select the IMG so native Delete/Backspace removes it
      const range = document.createRange();
      range.selectNode(e.target);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  const handleKeyDown = (e) => {
    const editor = ref.current;
    if (!editor) return;
    if (e.key !== "Delete" && e.key !== "Backspace") return;
    const selectedImg = editor.querySelector("img.is-selected");
    if (selectedImg) {
      e.preventDefault();
      selectedImg.remove();
      handleInput();
    }
  };

  const ensureSelectionInEditor = () => {
    const editor = ref.current;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !editor.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  const exec = (cmd) => {
    ensureSelectionInEditor();
    document.execCommand(cmd, false, null);
    handleInput();
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} className="px-2 py-1 rounded hover:bg-white/10 font-bold" data-testid={testId ? `${testId}-bold` : undefined}>B</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} className="px-2 py-1 rounded hover:bg-white/10 italic" data-testid={testId ? `${testId}-italic` : undefined}>I</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} className="px-2 py-1 rounded hover:bg-white/10 underline" data-testid={testId ? `${testId}-underline` : undefined}>U</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} className="px-2 py-1 rounded hover:bg-white/10" data-testid={testId ? `${testId}-bullet` : undefined}>• List</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} className="px-2 py-1 rounded hover:bg-white/10" data-testid={testId ? `${testId}-numbered` : undefined}>1. List</button>
        <label className="px-2 py-1 rounded hover:bg-white/10 cursor-pointer text-coral" style={{ color: "var(--coral)" }}>
          + Image
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={testId ? `${testId}-image-input` : undefined} />
        </label>
        <span className="text-xs text-slate-500 ml-2">Tip: paste screenshots · click image then press Delete to remove</span>
      </div>
      <div
        ref={ref}
        className="rich-editor"
        contentEditable
        data-placeholder={placeholder}
        data-empty="true"
        onInput={handleInput}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        suppressContentEditableWarning
      />
    </div>
  );
}
